import React, { memo } from 'react';
import { Typography, Timeline, Button, Tooltip, Tag, message } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  type SearchClientResult,
  usePutApiGuideviewsViewsGuideViewIdFeedback
} from '@/features/guides/api/generated';
import { logger } from '@/shared/utils/logger';

const { Text, Title } = Typography;

interface SearchResultCardProps {
  result: SearchClientResult;
  guideViewId?: string;
  onFeedbackSolved?: () => void;
  onFeedbackNotSolved?: () => void;
  onNavigateToNext?: () => void;
  requestedLocale?: string;
  feedbackType?: boolean; // true = solved, false = not solved, undefined = no feedback yet
  searchVehicle?: {
    manufacturer?: string | null;
    modelSeries?: string | null;
    engineBaseType?: string | null;
    modelYear?: number | null;
    euroCode?: string | null;
  };
}

export const SearchResultCard: React.FC<SearchResultCardProps> = memo(({ result, searchVehicle, guideViewId, onFeedbackSolved, onFeedbackNotSolved, onNavigateToNext, requestedLocale, feedbackType }) => {
  const { t } = useTranslation();
  const feedbackMutation = usePutApiGuideviewsViewsGuideViewIdFeedback();

  const isDemo = import.meta.env.VITE_APP_MODE === 'demo';

  // Select the appropriate content based on requested locale
  const getLocalizedContent = () => {
    if (!result.guide?.content) return null;

    const guide = result.guide;

    // If no locale requested, use default content
    if (!requestedLocale) {
      return guide.content;
    }

    // If English is requested, use translatedToEnglish
    if (requestedLocale.startsWith('en') && guide.translatedToEnglish) {
      return guide.translatedToEnglish;
    }

    // Otherwise use content (for Swedish and other languages)
    return guide.content;
  };

  const handleFeedback = (solved: boolean) => {
    if (feedbackType !== undefined) return; // Already gave feedback

    // Handle "not solved" feedback - navigate to next guide
    if (!solved) {
      // Update state immediately before doing anything else
      if (onFeedbackNotSolved) {
        onFeedbackNotSolved(); // This updates state immediately
      }

      message.info(t('demo.searchResults.tryingNextGuide'));

      // In demo mode, send actual feedback if we have a guideViewId
      if (isDemo && guideViewId) {
        feedbackMutation.mutate(
          {
            guideViewId: guideViewId,
            data: {
              guideViewId: guideViewId,
              resolutionFeedback: 'NotSolved'
            }
          },
          {
            onSuccess: () => {
              logger.debug('Feedback sent, moved to next guide', { guideViewId });
            },
            onError: (error) => {
              message.error(t('demo.searchResults.feedbackError'));
              logger.error('Failed to send feedback', error);
            }
          }
        );
      }

      // Navigate to next guide after a delay
      if (onNavigateToNext) {
        onNavigateToNext();
      }

      return;
    }

    // Handle "solved" feedback
    // Update state immediately
    if (onFeedbackSolved) {
      onFeedbackSolved(); // This will trigger view tracking and update state immediately
    }

    if (!isDemo) {
      message.success(t('demo.searchResults.feedbackThanks'));
      return;
    }

    // In demo mode, send actual feedback if we have a guideViewId
    if (!guideViewId) return;

    message.success(t('demo.searchResults.feedbackThanks'));
    feedbackMutation.mutate(
      {
        guideViewId: guideViewId,
        data: {
          guideViewId: guideViewId,
          resolutionFeedback: 'Solved'
        }
      },
      {
        onSuccess: () => {
          logger.debug('Feedback sent', { guideViewId, solved: true });
        },
        onError: (error) => {
          message.error(t('demo.searchResults.feedbackError'));
          logger.error('Failed to send feedback', error);
        }
      }
    );
  };

  const content = getLocalizedContent();

  if (!content) {
    return null;
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
      {/* Top bar with tags and info icon */}
      {(() => {
        const matchedFilter = result.matchedFilter;
        const hasVehicleTags = searchVehicle?.manufacturer ||
          searchVehicle?.modelSeries ||
          searchVehicle?.engineBaseType ||
          searchVehicle?.modelYear ||
          searchVehicle?.euroCode;

        // Check if search year matches the filter
        const searchYear = searchVehicle?.modelYear;
        const isYearMatch = searchYear && matchedFilter && (
          (matchedFilter.modelYearMin && matchedFilter.modelYearMax && searchYear >= matchedFilter.modelYearMin && searchYear <= matchedFilter.modelYearMax) ||
          (matchedFilter.modelYearMin && !matchedFilter.modelYearMax && searchYear >= matchedFilter.modelYearMin) ||
          (!matchedFilter.modelYearMin && matchedFilter.modelYearMax && searchYear <= matchedFilter.modelYearMax)
        );

        return (hasVehicleTags || result.motivation) && (
          <div className="vehicle-compatibility-bar" style={{
            backgroundColor: '#f5f5f5',
            padding: '12px 12px',
            margin: '-12px -12px 24px -12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 8,
          }}>
            {/* Vehicle tags on the left (or empty div for spacing) */}
            {hasVehicleTags && searchVehicle ? (
              matchedFilter ? (
                // Show vehicle compatibility with green/gray pills
                <div className="vehicle-tags" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '13px',
                    color: '#666',
                    marginRight: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('demo.searchResults.matchedFilter')}
                  </span>
                  {/* Show manufacturer from search */}
                  {searchVehicle.manufacturer && (
                    <Tag color={
                      matchedFilter.manufacturer && searchVehicle.manufacturer === matchedFilter.manufacturer
                        ? 'green'
                        : 'default'
                    }>
                      {searchVehicle.manufacturer}
                    </Tag>
                  )}
                  {/* Show modelSeries from search */}
                  {searchVehicle.modelSeries && (
                    <Tag color={
                      matchedFilter.modelSeries && searchVehicle.modelSeries === matchedFilter.modelSeries
                        ? 'green'
                        : 'default'
                    }>
                      {searchVehicle.modelSeries}
                    </Tag>
                  )}
                  {/* Show engineBaseType from search */}
                  {searchVehicle.engineBaseType && (
                    <Tag color={
                      matchedFilter.engineBaseType && searchVehicle.engineBaseType === matchedFilter.engineBaseType
                        ? 'green'
                        : 'default'
                    }>
                      {searchVehicle.engineBaseType}
                    </Tag>
                  )}
                  {/* Show modelYear from search */}
                  {searchVehicle.modelYear && (
                    <Tag color={isYearMatch ? 'green' : 'default'}>
                      {searchVehicle.modelYear}
                    </Tag>
                  )}
                  {/* Show euroCode from search */}
                  {searchVehicle.euroCode && (
                    <Tag color={
                      matchedFilter.euroCode && searchVehicle.euroCode === matchedFilter.euroCode
                        ? 'green'
                        : 'default'
                    }>
                      {searchVehicle.euroCode}
                    </Tag>
                  )}
                </div>
              ) : (
                // No matched filter - show universal guide tag
                <div className="vehicle-tags" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '13px',
                    color: '#666',
                    marginRight: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('demo.searchResults.matchedFilter')}
                  </span>
                  <Tag color="blue">{t('demo.searchResults.universalGuide')}</Tag>
                </div>
              )
            ) : (
              <div></div>
            )}

            {/* Info icon on the right */}
            {result.motivation && (
              <Tooltip title={result.motivation}>
                <InfoCircleOutlined style={{ fontSize: 18, color: '#666', cursor: 'help' }} />
              </Tooltip>
            )}
          </div>
        );
      })()}

      {/* Main content */}
      <div className="search-result-card" style={{ padding: '0 clamp(16px, 4vw, 32px) clamp(16px, 3vw, 24px) clamp(16px, 4vw, 32px)' }}>
        {/* Title */}
        <div style={{ textAlign: 'left', marginBottom: 24 }}>
          <Title level={3} style={{ color: '#000', marginBottom: 8 }}>
            {content.problem}
          </Title>
        </div>

        {/* Solutions Timeline */}
        <div>
          <Title level={5} style={{ color: '#000', marginBottom: 24 }}>
            {t('demo.searchResults.stepsToFollow')}
          </Title>
          <Timeline
            style={{ marginLeft: 8 }}
            items={content.solutions?.map((solution: string, index: number) => ({
              children: (
                <Text style={{ color: '#000', fontSize: 16 }}>
                  {solution}
                </Text>
              ),
              dot: (
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#9fe970',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#082a0b',
                  fontWeight: 'bold',
                  fontSize: 12
                }}>
                  {index + 1}
                </div>
              )
            }))}
          />
        </div>

        {/* Possible Cause */}
        {content.possibleCause && (
          <div style={{ marginBottom: 32, marginTop: -10 }}>
            <Text style={{ color: '#666' }}>
              <strong>{t('demo.searchResults.possibleCauseLabel')}</strong> {content.possibleCause}
            </Text>
          </div>
        )}

      </div>


      {/* Feedback Section - Show in both modes */}
      <div className="feedback-section" style={{ textAlign: 'center', backgroundColor: '#f0fce8', padding: 'clamp(24px, 5vw, 32px) clamp(12px, 3vw, 16px)', margin: '0 calc(-1 * clamp(16px, 4vw, 25px)) clamp(16px, 3vw, 25px)' }}>
        <Title level={5} style={{ color: '#000', marginBottom: 16 }}>
          {t('demo.searchResults.didThisGuideSolve')}
        </Title>
        <div className="feedback-buttons" style={{ display: 'flex', gap: 'clamp(12px, 3vw, 24px)', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            className="feedback-button"
            type="primary"
            size="large"
            icon={<CheckOutlined />}
            disabled={feedbackType !== undefined || (isDemo && !guideViewId)}
            loading={feedbackMutation.isPending}
            onClick={() => handleFeedback(true)}
            style={{
              backgroundColor: feedbackType === false ? '#d9d9d9' : '#9fe970',
              borderColor: feedbackType === false ? '#d9d9d9' : '#9fe970',
              color: feedbackType === false ? '#8c8c8c' : '#082a0b',
              minWidth: 'clamp(140px, 40vw, 200px)',
              height: 48,
              flex: '1 1 auto',
              maxWidth: '100%'
            }}
          >
            {feedbackType === true ? t('demo.searchResults.feedbackSent') : t('demo.searchResults.yesFixed')}
          </Button>
          <Button
            className="feedback-button"
            size="large"
            icon={<CloseOutlined />}
            disabled={feedbackType !== undefined || (isDemo && !guideViewId)}
            loading={feedbackMutation.isPending}
            onClick={() => handleFeedback(false)}
            style={{
              backgroundColor: feedbackType === true ? '#d9d9d9' : '#d73527',
              borderColor: feedbackType === true ? '#d9d9d9' : '#d73527',
              color: feedbackType === true ? '#8c8c8c' : '#fff',
              minWidth: 'clamp(140px, 40vw, 200px)',
              height: 48,
              flex: '1 1 auto',
              maxWidth: '100%'
            }}
          >
            {feedbackType === false ? t('demo.searchResults.feedbackSent') : t('demo.searchResults.noProblemRemains')}
          </Button>
        </div>
      </div>
    </div>
  );
});

SearchResultCard.displayName = 'SearchResultCard';